/**
 * launchRules — per-workflow launch preconditions and display rules, one
 * home for the checks `InstanceDetailDrawer.handleLaunch` currently
 * interleaves inline with the generic launch spine.
 *
 * Three of the five predicates already had a single shared implementation
 * before this module existed and are re-exported here unchanged, so a
 * caller can pull all five from one place:
 *
 *   - `showSpLoginToggle` — `shouldShowSpToggle`,
 *     `~/common/workflows/shouldShowSpToggle`.
 *   - `showOversight` — `shouldShowOversight`,
 *     `~/common/workflows/shouldShowOversight`.
 *   - `requiresClient` — NOT `workflowRequiresClient`
 *     (`~/common/workflows/launch/launchGuards`), which is a DIFFERENT,
 *     wider gate (3 ids, incl. `mortgagee_update_v1_2`) used to disable a
 *     catalog card before a workflow is even opened. This is the launch-TIME
 *     hard block `handleLaunch` runs inline
 *     (`InstanceDetailDrawer.tsx` 1328–1338: "Scoped to the mortgagee-update
 *     ids ONLY (R3)") — 2 ids, `mortgagee_update_v1_2` excluded because that
 *     generation gates on SP-credential readiness instead. COPIED from
 *     `isMortgageeUpdateWorkflow` / `MORTGAGEE_UPDATE_WORKFLOWS`,
 *     `InstanceDetailDrawer.tsx` 585–597 — add-in-local, not exported
 *     anywhere today. Phase 3 deletes the add-in's copy and calls this one.
 *
 * The other two are COPIED from `InstanceDetailDrawer.tsx` (add-in-local,
 * not exported anywhere today; Phase 3 deletes both copies):
 *
 *   - `requiresGraphMessageId` — `NEEDS_MAIL_ITEM_WORKFLOWS`,
 *     `InstanceDetailDrawer.tsx` 1283–1293: workflows that reply to the
 *     triggering email and can only launch from an Outlook mail item.
 *   - `isEndorsementFamily` — `isEndorsementWorkflow`,
 *     `InstanceDetailDrawer.tsx` 575–583: id-substring family check used for
 *     display (CarrierHandoff / EndorsementHistory sections), not a launch
 *     gate.
 *
 * Pure: no React, no Office.js, no network.
 */
import {
  shouldShowOversight,
  type OversightCatalogEntryLike,
} from "~/common/workflows/shouldShowOversight";
import {
  shouldShowSpToggle,
  type SpToggleCatalogEntryLike,
} from "~/common/workflows/shouldShowSpToggle";

export interface LaunchRuleCatalogEntryLike {
  id?: string | null;
  ref?: string | null;
}

/**
 * Mortgagee-update workflow ids (v1 + v2) whose prefill path threads the
 * selected client's chosen policy into `already_extracted` — with no client
 * selected there is nothing to thread, so `requiresClient` blocks the launch
 * entirely. Scoped to this exact set (R3); `mortgagee_update_v1_2` is
 * deliberately NOT a member — see the module docstring.
 */
export const MORTGAGEE_UPDATE_WORKFLOWS: ReadonlySet<string> = new Set([
  "carrier_mortgagee_update",
  "carrier_mortgagee_update_v2",
]);

/**
 * Workflows that reply to the triggering email and therefore need
 * `graph_message_id` (silent capture from `Office.context.mailbox.item.itemId`).
 * They cannot run from non-mail surfaces (calendar / contacts panes) or
 * before the mailbox item has been read.
 */
export const NEEDS_MAIL_ITEM_WORKFLOWS: ReadonlySet<string> = new Set([
  "test_draft_reply_email_setup",
  "carrier_mortgagee_update",
]);

/** True when this workflow's launch requires a client to already be selected. */
export function requiresClient(
  entry: LaunchRuleCatalogEntryLike | null | undefined,
): boolean {
  const id = entry?.id;
  return id != null && MORTGAGEE_UPDATE_WORKFLOWS.has(id);
}

/** True when this workflow needs `graph_message_id` (an Outlook mail-item id)
 *  to launch. */
export function requiresGraphMessageId(
  entry: LaunchRuleCatalogEntryLike | null | undefined,
): boolean {
  const id = entry?.id;
  return id != null && NEEDS_MAIL_ITEM_WORKFLOWS.has(id);
}

/** Whether the launch surface should render the dedicated-Service-Principal
 *  toggle (and send `use_sp_login`). Re-export of the existing shared check —
 *  see the module docstring. */
export function showSpLoginToggle(
  entry: SpToggleCatalogEntryLike | null | undefined,
): boolean {
  return shouldShowSpToggle(entry);
}

/** Whether the launch surface should render/send the Oversight Level
 *  selector. Re-export of the existing shared check — see the module
 *  docstring. */
export function showOversight(
  entry: OversightCatalogEntryLike | null | undefined,
): boolean {
  return shouldShowOversight(entry);
}

/**
 * Id-substring family check — endorsement / mortgagee / carrier workflows,
 * used for DISPLAY (which sections a terminal run shows), not a launch gate.
 */
export function isEndorsementFamily(workflowId?: string | null): boolean {
  if (!workflowId) return false;
  const w = workflowId.toLowerCase();
  return (
    w.includes("endorsement") ||
    w.includes("mortgagee") ||
    w.includes("carrier")
  );
}
